import {useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {useApiSettled} from '../../../util/useApiSettled';

// Anti-flash buffer: prevents the overlay from hiding between rapid API settle waves.
const SETTLE_DELAY_MS = 150;
// Guards against stale isApiSettled=true after a route change.  Must be >= the
// apiRegistry debounceTime (500ms) so settled$ reflects newly-fired API calls.
// Skipped once isApiSettled has cycled false->true (genuine settle), because the
// apiRegistry's own debounce already provides the same guarantee.
const NAVIGATION_HOLD_MS = 500;

/**
 * Tracks whether the current page's API calls have "stably settled".
 *
 * Lifecycle per route:
 *   1. Route changes -> reset everything, start the navigation hold.
 *   2. While isApiSettled is false, mark that we have been unsettled.
 *   3. When isApiSettled flips to true:
 *       - If we've already been unsettled (genuine cycle), wait only SETTLE_DELAY_MS.
 *       - Otherwise wait NAVIGATION_HOLD_MS + SETTLE_DELAY_MS to guard against
 *         a stale true that arrived before new API calls registered.
 *   4. Once settled for a route, lock it -- subsequent in-page API churn
 *      (body/option changes) is ignored until the next route change.
 *
 * @returns {boolean} true once APIs are stably settled for the current route
 */
export const useSettleState = () => {
	const {isSettled: isApiSettled} = useApiSettled();
	const {pathname} = useLocation();

	const [isSettled, setIsSettled] = useState(false);

	const timerRef = useRef(null);
	const prevPathnameRef = useRef(pathname);
	const hasBeenUnsettledRef = useRef(false);
	const hasSettledOnceRef = useRef(false);

	// Synchronous route-change reset — runs during render (before paint)
	// so the overlay stays visible between sections with no content flash.
	if (pathname !== prevPathnameRef.current) {
		prevPathnameRef.current = pathname;
		hasBeenUnsettledRef.current = false;
		hasSettledOnceRef.current = false;
		clearTimeout(timerRef.current);
		timerRef.current = null;
		if (isSettled) setIsSettled(false);
	}

	useEffect(() => {
		if (hasSettledOnceRef.current) return;

		if (!isApiSettled) {
			hasBeenUnsettledRef.current = true;
			return;
		}

		const delay = hasBeenUnsettledRef.current ? SETTLE_DELAY_MS : NAVIGATION_HOLD_MS + SETTLE_DELAY_MS;

		timerRef.current = setTimeout(() => {
			setIsSettled(true);
			hasSettledOnceRef.current = true;
		}, delay);

		return () => clearTimeout(timerRef.current);
	}, [isApiSettled, pathname]);

	return isSettled;
};
